import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Layers, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/States";
import { SelectionForm } from "@/components/SelectionForm";
import { UpgradeNote } from "@/components/UpgradeNote";
import { usePlan } from "@/lib/plan";
import { api, fetchSubjects, formatError, runJob } from "@/lib/api";

export default function Flashcards() {
  const { limits } = usePlan();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [subjects, setSubjects] = useState(null);
  const [decks, setDecks] = useState(null);
  const [sel, setSel] = useState({ subject_id: params.get("subject") || "", topic: params.get("topic") || "", difficulty: params.get("difficulty") || "beginner", count: 10 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const loadDecks = () => api.get("/flashcards").then((r) => setDecks(r.data)).catch((e) => setErr(formatError(e)));
  useEffect(() => { fetchSubjects().then(setSubjects); loadDecks(); }, []);

  const generate = async () => {
    setBusy(true); setErr("");
    try {
      const data = await runJob("/flashcards/generate", sel);
      toast.success(`Deck saved with ${data.cards.length} cards`);
      nav(`/flashcards/${data.id}`);
    } catch (e) { setErr(formatError(e)); setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/flashcards/${id}`); setDecks((d) => d.filter((x) => x.id !== id)); toast.success("Deck deleted"); } catch (e) { toast.error(formatError(e)); }
  };

  if (!subjects || !decks) return err ? <ErrorState message={err} /> : <Loading />;
  return (
    <div>
      <PageHeader eyebrow="Flashcards" title="Revise with flashcards" text="Generate concise Q&A cards, then mark each one as known or needing revision." />
      <div className="rounded-2xl border border-line bg-surface p-6">
        {limits && !limits.flashcards && <div className="mb-5"><UpgradeNote testId="flashcards-locked" text="AI flashcard generation is included in LearnAI Plus and Pro. Your saved decks stay available to study." /></div>}
        <SelectionForm limits={limits} subjects={subjects} value={sel} onChange={setSel} counts={[5, 10, 20]} />
        {err && <div className="mt-5"><ErrorState message={err} /></div>}
        <Button data-testid="flashcards-generate-btn" disabled={!sel.subject_id || !sel.topic || busy || (limits && !limits.flashcards)} onClick={generate} className="mt-6 rounded-full px-6">
          {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating deck...</> : <><Layers className="mr-2 h-4 w-4" />Generate & save deck</>}
        </Button>
      </div>
      <h3 className="mb-4 mt-10 text-xl font-bold text-ink">Your saved decks</h3>
      {decks.length === 0 ? <EmptyState testId="decks-empty" title="No decks yet" text="Generate your first deck above. It will be saved to your account." /> : (
        <div data-testid="decks-list" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {decks.map((d) => (
            <div key={d.id} data-testid={`deck-card-${d.id}`} className="card-hover flex flex-col rounded-2xl border border-line bg-surface p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-aqua">{d.subject_name}</p>
              <h4 className="mt-2 font-bold text-ink">{d.topic}</h4>
              <p className="mt-1 text-xs capitalize text-ink-3">{d.difficulty} · {d.total} cards</p>
              <div className="mt-4 flex h-1.5 overflow-hidden rounded-full bg-elevated">
                <div className="bg-emerald-500" style={{ width: `${(d.known / d.total) * 100}%` }} />
                <div className="bg-amber-400" style={{ width: `${(d.revise / d.total) * 100}%` }} />
              </div>
              <p className="mt-2 text-xs text-ink-3">{d.known} known · {d.revise} to revise</p>
              <div className="mt-4 flex gap-2">
                <Button asChild size="sm" className="flex-1 rounded-full"><Link data-testid={`deck-open-${d.id}`} to={`/flashcards/${d.id}`}>Study</Link></Button>
                <Button data-testid={`deck-delete-${d.id}`} size="sm" variant="ghost" onClick={() => remove(d.id)} aria-label="Delete deck"><Trash2 className="h-4 w-4 text-ink-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
